'use client';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import fr from 'react-phone-number-input/locale/fr.json';

import { Button } from '../../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Input } from '../../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { ScrollArea } from '../../../ui/scroll-area';

/**
 * Props for the PhoneInput component.
 *
 * International phone input with a searchable country selector dropdown.
 * Country flags include a `title` attribute for accessibility. Defaults to FR locale.
 * Returns values in E.164 format (e.g. "+33612345678").
 */
type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'ref' | 'value'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    /** Accessible name for the country selector button (announced by AT). */
    countrySelectorLabel?: string;
    /** Called with the phone number in E.164 format, or undefined when cleared. */
    onChange?: (value: RPNInput.Value | undefined) => void;
  };

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  countrySelectorLabel?: string;
  disabled?: boolean;
  labels?: {
    empty: string;
    placeholder: string;
  };
  onChange: (country: RPNInput.Country) => void;
  options: CountryEntry[];
  value: RPNInput.Country;
};

type CountrySelectOptionProps = RPNInput.FlagProps & {
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
  selectedCountry: RPNInput.Country;
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];
  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {!!Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = 'FlagComponent';

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
  <Input
    className={cn('rounded-e-lg rounded-s-none', className)}
    {...props}
    ref={ref}
  />
));
InputComponent.displayName = 'InputComponent';

const CountrySelectOption = ({
  country,
  countryName,
  onChange,
  onSelectComplete,
  selectedCountry,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">
        {`+${RPNInput.getCountryCallingCode(country)}`}
      </span>
      <CheckIcon
        className={cn(
          'ml-auto size-4',
          country === selectedCountry ? 'opacity-100' : 'opacity-0'
        )}
      />
    </CommandItem>
  );
};
CountrySelectOption.displayName = 'CountrySelectOption';

const CountrySelect = ({
  countrySelectorLabel = 'Sélecteur de pays',
  disabled,
  labels = {
    empty: 'Pas de pays trouvé',
    placeholder: 'Chercher un pays...',
  },
  onChange,
  options: countryList,
  value: selectedCountry,
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  // Lock the selector when there's a single selectable country (e.g. a consumer
  // passing `countries={['FR']}`): the dropdown would only ever offer that one
  // country, so it becomes a static, non-interactive flag (no click target).
  // Multi-country selectors — the default — are unaffected.
  const isLocked =
    !!disabled || countryList.filter((entry) => entry.value).length <= 1;

  return (
    <Popover modal open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={countrySelectorLabel}
          className={cn(
            'flex gap-1 rounded-e-none rounded-s-lg border-r-0 px-3 focus:z-10 lg:h-10',
            // Locked via a single country (not a disabled field): keep it
            // full-opacity — it's non-interactive, not an error/disabled state.
            isLocked && !disabled && 'opacity-100'
          )}
          disabled={isLocked}
          type="button"
          variant="outline"
        >
          <span aria-hidden="true" className="contents">
            <FlagComponent
              country={selectedCountry}
              countryName={selectedCountry}
            />
            <ChevronsUpDown
              className={cn(
                '-mr-2 size-4 opacity-50',
                isLocked ? 'hidden' : 'opacity-100'
              )}
            />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            placeholder={labels.placeholder}
            value={searchValue}
            onValueChange={(value) => {
              setSearchValue(value);
              setTimeout(() => {
                const viewport =
                  scrollAreaRef.current?.querySelector<HTMLElement>(
                    '[data-radix-scroll-area-viewport]'
                  );
                if (viewport) viewport.scrollTop = 0;
              }, 0);
            }}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>{labels.empty}</CommandEmpty>
              <CommandGroup>
                {countryList.map(
                  ({ label, value }) =>
                    value && (
                      <CountrySelectOption
                        key={value}
                        country={value}
                        countryName={label}
                        selectedCountry={selectedCountry}
                        onChange={onChange}
                        onSelectComplete={() => setIsOpen(false)}
                      />
                    )
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
CountrySelect.displayName = 'CountrySelect';

export const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    {
      className,
      countrySelectorLabel,
      defaultCountry = 'FR',
      onChange,
      value,
      ...props
    },
    ref
  ) => (
    <RPNInput.default
      ref={ref}
      className={cn('flex', className)}
      countrySelectComponent={CountrySelect}
      countrySelectProps={{ countrySelectorLabel }}
      defaultCountry={defaultCountry}
      flagComponent={FlagComponent}
      inputComponent={InputComponent}
      labels={fr}
      smartCaret={false}
      value={value || undefined}
      onChange={(value) => onChange?.(value)}
      {...props}
    />
  )
);
PhoneInput.displayName = 'PhoneInput';
